import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';

const UserManager = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'STAFF' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [targetId, setTargetId] = useState(null);

  const refreshData = async () => {
    setLoading(true);
    try {
      const data = await userService.getUsers();
      setAllUsers(data || []);
    } catch (error) {
      console.error("사용자 로드 실패:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    refreshData();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'STAFF' });
    setIsEditMode(false);
    setTargetId(null);
  };

  const handleApprove = async (id) => {
    if (!window.confirm("이 사용자의 가입을 승인하시겠습니까?")) return;
    const result = await userService.approveUser(id);
    if (result.success) {
      alert("✅ 사용자가 승인되었습니다.");
      refreshData();
    } else {
      alert("승인 중 오류가 발생했습니다.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let result;
    if (isEditMode) {
      // 수정 모드: 이름/권한만 변경 (비밀번호·이메일은 인증 쪽에서 관리)
      result = await userService.updateUser(targetId, formData);
    } else {
      // 신규 등록: 정식 인증 계정 생성 + 승인상태 프로필
      result = await userService.addDirectUser({ ...formData, status: 'APPROVED' });
    }

    if (result && result.success) {
      alert(isEditMode ? "✅ 정보가 수정되었습니다." : "✅ 신규 사용자가 등록되었습니다.");
      resetForm();
      refreshData();
    } else {
      alert((result && result.message) || "저장에 실패했습니다.");
    }
    setLoading(false);
  };

  // 비밀번호 재설정 메일 발송 (관리자가 타인 비번을 직접 바꿀 수 없으므로 재설정 링크를 메일로)
  const handleSendReset = async (email) => {
    if (!email) return alert("이메일 정보가 없습니다.");
    if (!window.confirm(`${email} 주소로 비밀번호 재설정 메일을 보낼까요?`)) return;
    const result = await userService.sendPasswordReset(email);
    if (result.success) {
      alert("✅ 재설정 메일을 보냈습니다. 해당 사용자가 메일에서 새 비밀번호를 설정할 수 있습니다.");
    } else {
      alert(result.message || "메일 발송에 실패했습니다.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("정말 삭제하시겠습니까? 로그인 및 엔지니어 연동이 해제됩니다.")) {
      const result = await userService.deleteUser(id);
      if (result.success) {
        refreshData();
      } else {
        alert("삭제에 실패했습니다.");
      }
    }
  };

  const styles = {
    card: { background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: '25px' },
    input: { width: '100%', padding: '10px', marginBottom: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' },
    btn: (bg) => ({ padding: '8px 15px', background: bg, color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }),
    badge: (status) => ({ 
      padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold',
      background: status === 'APPROVED' ? '#dcfce7' : '#fee2e2',
      color: status === 'APPROVED' ? '#16a34a' : '#ef4444'
    })
  };

  return (
    <div style={{ padding: '10px' }}>
      <h2 style={{ marginBottom: '25px' }}>👥 사용자 및 권한 관리 (Cloud)</h2>

      <div style={styles.card}>
        <h4 style={{ marginTop: 0 }}>{isEditMode ? '👤 정보 수정' : '➕ 신규 사용자 등록'}</h4>
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input style={{ ...styles.input, flex: 1 }} placeholder="성함" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
          <input
            style={{ ...styles.input, flex: 1, background: isEditMode ? '#f1f5f9' : 'white' }}
            type="email"
            placeholder="이메일"
            value={formData.email}
            onChange={e => setFormData({...formData, email: e.target.value})}
            required
            disabled={isEditMode}
            title={isEditMode ? '이메일(로그인 아이디)은 변경할 수 없습니다.' : ''}
          />
          {/* 비밀번호는 신규 등록 시에만 입력 (수정 시에는 재설정 메일로 변경) */}
          {!isEditMode && (
            <input style={{ ...styles.input, flex: 1 }} type="password" placeholder="비밀번호(6자 이상)" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required minLength={6} />
          )}
          <select style={{ ...styles.input, flex: 1 }} value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}>
            <option value="STAFF">일반 직원</option>
            <option value="RECALL">리콜 수행자</option>
            <option value="ADMIN">관리자</option>
            <option value="VIEWER">외부 열람(모니터링 전용)</option>
          </select>
          <div style={{ width: '100%', textAlign: 'right' }}>
            {isEditMode && (
              <span style={{ fontSize: '11px', color: '#94a3b8', marginRight: '12px' }}>
                * 비밀번호 변경은 목록의 '비번재설정' 버튼(메일 발송)을 이용하세요.
              </span>
            )}
            <button type="submit" disabled={loading} style={styles.btn('#1e293b')}>
              {loading ? '처리 중...' : (isEditMode ? '수정 완료' : '사용자 추가')}
            </button>
            {isEditMode && (
              <button type="button" onClick={resetForm} style={{ ...styles.btn('#94a3b8'), marginLeft: '8px' }}>
                취소
              </button>
            )}
          </div>
        </form>
      </div>

      <div style={styles.card}>
        <h4 style={{ marginTop: 0 }}>✅ 등록된 사용자 목록</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #f1f5f9' }}>
              <th style={{ padding: '12px' }}>사용자 정보</th>
              <th style={{ padding: '12px' }}>권한</th>
              <th style={{ padding: '12px' }}>상태</th>
              <th style={{ padding: '12px', textAlign: 'center' }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {allUsers.map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                <td style={{ padding: '12px' }}>
                  <b>{user.name}</b><br/><span style={{ fontSize: '11px', color: '#94a3b8' }}>{user.email}</span>
                </td>
                <td style={{ padding: '12px' }}>{user.role}</td>
                <td style={{ padding: '12px' }}><span style={styles.badge(user.status)}>{user.status === 'APPROVED' ? '승인완료' : '대기중'}</span></td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  {user.status === 'PENDING' && <button onClick={() => handleApprove(user.id)} style={{...styles.btn('#11caa0'), marginRight:'5px'}}>승인</button>}
                  <button onClick={() => { setIsEditMode(true); setTargetId(user.id); setFormData({...user}); }} style={{...styles.btn('#3b82f6'), marginRight:'5px'}}>수정</button>
                  <button onClick={() => handleSendReset(user.email)} style={{...styles.btn('#f59e0b'), marginRight:'5px'}}>비번재설정</button>
                  {user.email !== 'admin@e2bility.net' && <button onClick={() => handleDelete(user.id)} style={styles.btn('#ef4444')}>삭제</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManager;